import {makeAutoObservable, runInAction} from "mobx";
import CustomersService from "../../../services/api/customer-service";
import {rootContainer, TYPE} from "../../../services/container/root";
import CustomerStore, { _CUSTOMER_EMPTY_ID_ } from "./customer-store";

class CustomersMainStore {
	private _customers: {[id: string]: CustomerStore} = {};
	
	constructor() {
		makeAutoObservable(this);
	}
	
	private get service(): CustomersService { return rootContainer.get<CustomersService>(TYPE.CustomersService); }
	
	/** when the method is called it creates customer store immediatelly before sending request, this allows users to immediatelly interacts with customer store */
	public async fetchCustomersByIds(customersIds: string[], excludeProfile = false) {
		let filteredIds: string[] = [];
		try {
			/** preventing same id in the list by using filteredIdMap, after reducing the map should contain only the ids we need to request from server */
			const filteredIdMap: { [id: string]: string } = {};
			
			customersIds.reduce((filteredIdMap, id) => {
				/** convert requested ids like null, empty string and undefined to same constant value, used here and in getCustomerStore to identify the store */
				id = toValidId(id);
				
				/** filtering our existing ids, when the store for it already exists */
				const existingStore = this._customers[id];
				if (existingStore) {
					/** a store fetched without the shopper profile can not serve a request that needs it */
					if (excludeProfile || !existingStore.isProfileExcluded) {
						return filteredIdMap;
					}
					existingStore.setIsLoading(true);
					filteredIdMap[id] = id;
					return filteredIdMap;
				}

				/** create store for each new requested id and set initial state */
				this._customers[id] = new CustomerStore(id);
				/** do not request empty id customer */
				if (id === _CUSTOMER_EMPTY_ID_) {
					this._customers[id].setNotExists();
					return filteredIdMap;
				}

				this._customers[id].setIsLoading(true);
				filteredIdMap[id] = id;
				return filteredIdMap;

			}, filteredIdMap);

			filteredIds = Object.values(filteredIdMap);
			if (filteredIds.length === 0) {
				return;
			}

			const customers = await this.service.fetchCustomers(filteredIds, excludeProfile);
			runInAction(() => {
				filteredIds.forEach(id => {
					const model = customers.find(c => c.denormalized_id === id);
					this._customers[id].setIsLoading(false);
					if (model) {
						/** set model if found, otherwise - set as not found customer */
						this._customers[id].setModel(model, excludeProfile);
					}
					else {
						this._customers[id].setNotFound();
					}
				});
			})
		}
		catch (e) {
			runInAction(() => {
				filteredIds.forEach(id => {
					this._customers[id].setIsLoading(false);
					delete this._customers[id]; /** removing requested customer store from the list to allow it to be fetched again */
				});
			});
		}
	}

	public getCustomerStore(id: string): CustomerStore | undefined {
		id = toValidId(id);
		return this._customers[id];
	}	
}

const customersMainStore = new CustomersMainStore();
export default customersMainStore;


function isEmptyId(id:string|undefined|null): boolean {
	return id === null || id === undefined || (id + '').trim() === '';
}

/** convert empty id to _CUSTOMER_EMPTY_ID_ and make sure the id is of type string */
function toValidId(id: string): string {
	return isEmptyId(id) ? _CUSTOMER_EMPTY_ID_ : (id + '');
}